import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { PhotoIcon, MicrophoneIcon } from '@heroicons/react/24/solid';
import { uploadToCloudinary } from '../../utils/cloudinary';
import AudioRecorder from '../audio/AudioRecorder';
import AudioPlayer from '../audio/AudioPlayer';
import { uploadAudio, generateAudioFileName, getAudioDuration } from '../../utils/audioUtils';
import { useTranslation } from 'react-i18next';

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  imageUrl?: string;
  audioUrl?: string;
  audioDuration?: number;
  createdAt: any;
  read: boolean;
}

interface Conversation {
  id: string;
  participants: string[];
  lastMessage: string;
  lastMessageTime: any;
  otherUser?: any;
}

interface MessageThreadProps {
  conversation: Conversation;
  currentUser: any;
}

const MessageThread: React.FC<MessageThreadProps> = ({ conversation, currentUser }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState<string | null>(null);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  // Cargar mensajes
  useEffect(() => {
    if (!conversation.id) return;

    const messagesQuery = query(
      collection(db, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesData: Message[] = [];
      snapshot.forEach((doc) => {
        const messageData = doc.data() as Omit<Message, 'id'>;
        if (messageData.conversationId === conversation.id) {
          messagesData.push({
            id: doc.id,
            ...messageData
          });
        }
      });
      setMessages(messagesData);
      scrollToBottom();
      markMessagesAsRead(messagesData);
    });

    return () => unsubscribe();
  }, [conversation.id, currentUser]);

  // Desplazarse al final de los mensajes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Asegurarse de que se cargue directamente al último mensaje
  useEffect(() => {
    scrollToBottom();
  }, []);

  // Marcar mensajes como leídos
  const markMessagesAsRead = async (messagesData: Message[]) => {
    if (!currentUser) return;

    const unreadMessages = messagesData.filter(
      (msg) => !msg.read && msg.senderId !== currentUser.uid
    );

    for (const message of unreadMessages) {
      await updateDoc(doc(db, 'messages', message.id), {
        read: true
      });
    }

    // Actualizar el contador de mensajes no leídos en la conversación
    if (unreadMessages.length > 0) {
      await updateDoc(doc(db, 'conversations', conversation.id), {
        unreadCount: 0
      });
    }
  };

  // Enviar un nuevo mensaje
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !fileInputRef.current?.files?.[0]) || !currentUser || isLoading) return;

    try {
      setIsLoading(true);
      let imageUrl = '';

      // Si hay una imagen seleccionada, subirla a Cloudinary sin mostrar indicador de carga
      if (fileInputRef.current?.files?.[0]) {
        try {
          imageUrl = await uploadToCloudinary(fileInputRef.current.files[0]);
        } catch (error) {
          console.error('Error al subir imagen:', error);
          alert(t('messages.imageUploadError'));
          setIsLoading(false);
          return;
        }
      }

      // Crear el nuevo mensaje
      await addDoc(collection(db, 'messages'), {
        conversationId: conversation.id,
        senderId: currentUser.uid,
        content: newMessage.trim(),
        imageUrl: imageUrl || '',
        createdAt: serverTimestamp(),
        read: false
      });

      // Actualizar la conversación con el último mensaje
      await updateDoc(doc(db, 'conversations', conversation.id), {
        lastMessage: imageUrl ? (newMessage.trim() ? `📷 Imagen: ${newMessage.trim()}` : '📷 Imagen') : newMessage.trim(),
        lastMessageTime: serverTimestamp()
      });

      // Limpiar el estado
      setNewMessage('');
      setSelectedImage(null);
      setSelectedImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      alert('Error al enviar el mensaje. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  // Manejar la grabación de audio
  const handleToggleAudioRecorder = () => {
    setShowAudioRecorder(prev => !prev);
  };

  // Esta función ya está definida más abajo

  // Manejar el envío de un mensaje de audio
  const handleSendAudio = async (audioBlob: Blob) => {
    if (!currentUser) return;
    setIsLoading(true);

    try {
      // Generar un nombre de archivo único
      const fileName = generateAudioFileName(currentUser.uid);

      // Subir el audio a Firebase Storage sin mostrar indicador de carga
      const audioUrl = await uploadAudio(audioBlob, fileName);

      // Obtener la duración del audio y asegurarse de que sea un número válido
      let audioDuration = await getAudioDuration(audioBlob);

      // Verificar que la duración sea un número válido
      if (isNaN(audioDuration) || !isFinite(audioDuration) || audioDuration <= 0) {
        audioDuration = 30; // Usar 30 segundos como valor predeterminado si hay problemas
      }

      // Crear el nuevo mensaje con el audio
      await addDoc(collection(db, 'messages'), {
        conversationId: conversation.id,
        senderId: currentUser.uid,
        content: '',
        audioUrl,
        audioDuration,
        createdAt: serverTimestamp(),
        read: false
      });

      // Actualizar la conversación con el último mensaje
      await updateDoc(doc(db, 'conversations', conversation.id), {
        lastMessage: '🎤 Mensaje de voz',
        lastMessageTime: serverTimestamp()
      });

      // Limpiar el estado
      setShowAudioRecorder(false);
    } catch (error) {
      console.error('Error al enviar audio:', error);
      alert('Error al enviar el audio. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  // Cancelar la grabación de audio
  const handleCancelAudioRecording = () => {
    setShowAudioRecorder(false);
  };

  const handleImageSelect = () => {
    fileInputRef.current?.click();
  };

  // Manejar la selección de imagen y mostrar vista previa
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Cancelar la selección de imagen
  const handleCancelImage = () => {
    setSelectedImage(null);
    setSelectedImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatMessageDate = (timestamp: any) => {
    if (!timestamp) return '';

    const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
    return formatDistanceToNow(date, { addSuffix: true, locale: es });
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Área de mensajes - Ocupa todo el espacio disponible con scroll independiente */}
      <div className="flex-grow overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
        {/* Información de última conexión */}
        {conversation.otherUser && (
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-2">
            {conversation.otherUser.isOnline
              ? t('messages.online')
              : conversation.otherUser.lastActive
                ? t('messages.lastSeen', { time: formatDistanceToNow(new Date(conversation.otherUser.lastActive), { addSuffix: true, locale: es }) })
                : t('messages.offline')
            }
          </p>
        )}

        {messages.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {t('messages.noMessages')}. {t('messages.beFirstToMessage')}!
            </p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              // Obtener la fecha del mensaje actual
              const messageDate = message.createdAt ?
                (message.createdAt instanceof Date ? message.createdAt : message.createdAt.toDate()) :
                new Date();
              const messageDay = messageDate.toLocaleDateString();

              // Verificar si necesitamos mostrar un separador de fecha
              const previousMessage = index > 0 ? messages[index - 1] : null;
              const previousMessageDate = previousMessage?.createdAt ?
                (previousMessage.createdAt instanceof Date ? previousMessage.createdAt : previousMessage.createdAt.toDate()) :
                null;
              const previousMessageDay = previousMessageDate ? previousMessageDate.toLocaleDateString() : null;

              // Determinar si este mensaje es parte de un grupo del mismo remitente
              const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;
              const isGroupStart = previousMessage?.senderId !== message.senderId;
              const isGroupEnd = nextMessage?.senderId !== message.senderId;

              return (
                <React.Fragment key={message.id}>
                  {/* Mostrar separador de fecha si es necesario */}
                  {messageDay !== previousMessageDay && (
                    <div className="flex justify-center my-2">
                      <div className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full text-xs text-gray-500 dark:text-gray-400">
                        {messageDay === new Date().toLocaleDateString() ? 'Hoy' : messageDay}
                      </div>
                    </div>
                  )}

                  {/* Mensaje */}
                  <div className={`flex ${message.senderId === currentUser.uid ? 'justify-end' : 'justify-start'}`}>
                    {/* Avatar para mensajes recibidos al inicio de un grupo */}
                    {message.senderId !== currentUser.uid && isGroupStart && (
                      <div className="flex-shrink-0 mr-2 self-end">
                        <img
                          src={conversation.otherUser?.photoURL || '/default-avatar.png'}
                          alt=""
                          className="h-6 w-6 rounded-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src = '/default-avatar.png';
                          }}
                        />
                      </div>
                    )}
                    {/* Espacio para alinear mensajes cuando no hay avatar */}
                    {message.senderId !== currentUser.uid && !isGroupStart && <div className="w-8"></div>}

                    <div
                      className={`max-w-[75%] rounded-lg px-2 py-1.5 ${isGroupStart ? 'mt-1' : 'mt-0.5'} ${message.senderId === currentUser.uid
                        ? `bg-domired-600 text-white ${isGroupEnd ? 'rounded-br-none' : 'rounded-br-lg'} ${isGroupStart ? 'rounded-tr-lg' : 'rounded-tr-none'}`
                        : `bg-gray-200 dark:bg-gray-700 ${isGroupEnd ? 'rounded-bl-none' : 'rounded-bl-lg'} ${isGroupStart ? 'rounded-tl-lg' : 'rounded-tl-none'}`
                        }`}
                    >
                      {message.content && (
                        <p className="break-words text-sm">{message.content}</p>
                      )}
                      {message.imageUrl && (
                        <img
                          src={message.imageUrl}
                          alt="Imagen compartida"
                          className="max-w-[250px] max-h-[300px] rounded-lg object-cover mt-1 cursor-pointer"
                          onClick={() => window.open(message.imageUrl, '_blank')}
                          onLoad={() => scrollToBottom()}
                        />
                      )}
                      {message.audioUrl && (
                        <div className="my-1">
                          <AudioPlayer
                            audioUrl={message.audioUrl}
                            duration={message.audioDuration}
                          />
                        </div>
                      )}
                      <div
                        className={`text-xs ${isGroupEnd ? 'inline-block' : 'hidden'} ${message.senderId === currentUser.uid
                          ? 'text-gray-200'
                          : 'text-gray-500 dark:text-gray-400'
                          }`}
                      >
                        {formatMessageDate(message.createdAt)}
                        {message.senderId === currentUser.uid && (
                          <span className="ml-1">
                            {message.read ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Grabador de audio */}
      {showAudioRecorder && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky bottom-0 z-10 flex-shrink-0">
          <AudioRecorder
            onAudioRecorded={handleSendAudio}
            onCancel={handleCancelAudioRecording}
          />
        </div>
      )}

      {/* Formulario para enviar mensajes - Estilo Instagram */}
      {!showAudioRecorder && (
        <form
          onSubmit={handleSendMessage}
          className="p-3 border-t border-gray-200 dark:border-gray-700 flex items-center bg-white dark:bg-gray-800 sticky bottom-0 z-10 flex-shrink-0"
        >
          {selectedImagePreview ? (
            <div className="w-full">
              <div className="relative mb-2">
                <img
                  src={selectedImagePreview}
                  alt="Vista previa"
                  className="max-h-[150px] rounded-lg mx-auto"
                />
                <button
                  type="button"
                  onClick={handleCancelImage}
                  className="absolute top-1 right-1 bg-gray-800 bg-opacity-70 text-white rounded-full p-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center w-full rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2 mb-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('messages.addOptionalText')}
                  className="flex-grow mx-2 py-1 bg-transparent focus:outline-none text-sm"
                  disabled={isLoading}
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="text-domired-600 hover:text-domired-700 disabled:opacity-50 font-semibold text-sm"
                  disabled={isLoading}
                >
                  {t('common.send')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center w-full rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2">
              {/* Botones de acciones a la izquierda */}
              <div className="flex items-center space-x-1">
                <button
                  type="button"
                  onClick={handleImageSelect}
                  className="text-gray-500 hover:text-domired-600 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                  disabled={isLoading}
                >
                  <PhotoIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Campo de texto */}
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t('messages.writeMessage')}
                className="flex-grow mx-2 py-1 bg-transparent focus:outline-none text-sm"
                disabled={isLoading}
              />

              {/* Botones de acción a la derecha */}
              <div className="flex items-center space-x-2">
                {/* Botón de grabación de audio */}
                <button
                  type="button"
                  onClick={handleToggleAudioRecorder}
                  className="text-gray-500 hover:text-domired-600 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                  disabled={isLoading}
                  aria-label="Grabar mensaje de voz"
                >
                  <MicrophoneIcon className="h-5 w-5" />
                </button>

                {/* Botón de enviar */}
                {newMessage.trim() || selectedImage ? (
                  <button
                    type="submit"
                    className="text-domired-600 hover:text-domired-700 disabled:opacity-50 font-semibold text-sm"
                    disabled={isLoading}
                  >
                    {t('common.send')}
                  </button>
                ) : null}
              </div>
            </div>
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
        </form>
      )}

      {/* Eliminamos el indicador de carga para una experiencia más limpia */}
    </div>
  );
};

export default MessageThread;
